import { intervalToDuration, parseISO } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import useStateRef from "react-usestateref";
import { FabrixIcon, zeroPadString } from "../../common";
import { useIcon } from "../../hooks";


export interface CountDownClockProps {
    time?: Date | string;
    className?: string;
    icon?: string;
    style?: React.CSSProperties;
    onZero?: () => void;
    active?: boolean;
}

/**
 * Renders a relative time display with an optional icon for activity timestamps.
 * @param props Activity clock configuration including the timestamp, CSS overrides, and icon choice.
 * @returns JSX element showing the elapsed time since the referenced timestamp.
 */
export default function CountDownClock(props: CountDownClockProps) {

    const icon = useIcon();
    const [_elapsedTime, setElapsedTime] = useState<string>('');
    const [_targetTime, setTargetTime, _targetTimeRef] = useStateRef<Date | string | undefined>(props.time)
    const [_active, setActive, _activeRef] = useStateRef<boolean | undefined>(props.active);

    /**
     * Produces a localized duration string or formatted date depending on elapsed time.
     * @param targetTime Source timestamp as a Date or ISO string.
     * @returns Human-readable elapsed duration or formatted date when older than one day.
     */
    const showDuration = (targetTime?: Date | string) => {
        if (targetTime && _activeRef.current) {
            if (typeof targetTime === 'string') {
                targetTime = parseISO(targetTime)
            }
            let isZero = true;

            let duration = intervalToDuration({ start: new Date(), end: targetTime })
            
            let values = new Array<string>();
            if (duration.days && duration.days >= 1)  {
                isZero = false;
                values.push(zeroPadString(2, duration.days ?? 0));
            }
            if  (duration.hours && duration.hours >= 1)  {
                isZero = false;
                values.push(zeroPadString(2, duration.hours ?? 0));
            }

            if (duration.minutes && duration.minutes > 0)  {
                isZero = false;
            }
            if (duration.seconds && duration.seconds > 0)  {
                isZero = false;
            }
            if (isZero) {
                setActive(false);
                props.onZero?.();
                return "00 : 00"
            }
            
            let minutes = zeroPadString(2, duration.minutes ?? 0);
            let seconds = zeroPadString(2, duration.seconds ?? 0);
            values.push(minutes);
            values.push(seconds);
            return values.join(' : ');
            
            
        }
        return '';
    };

    useEffect(() => {
        setActive(props.active);
    }, [props.active]);

    useEffect(() => {
        setTargetTime(props.time)
    }, [props.time])

    useEffect(() => {
        updateTimeLeft();
    }, [_targetTimeRef.current])

    /**
     * Updates the elapsed time display and schedules the next refresh.
     */
    const updateTimeLeft = useCallback(async () => {
        if (_targetTime && _activeRef.current) {
            let duration = showDuration(_targetTimeRef.current)
            setElapsedTime(duration);
            window.setTimeout(updateTimeLeft, 1 * 1000);
        }
    }, [_targetTime, _activeRef.current]);

    return (
        <div style={props.style} className={props.className}>
            <span style={{ marginRight: 6 }} className={icon(props.icon ?? FabrixIcon.clock)}></span>
            <span >{_elapsedTime}</span>

        </div>)

}